import { config } from '../Environment';

const API_BASE_URL = config.API_BASE;

export interface ImageData {
  url: string;
  key: string;
  mimeType: string;
  size: number;
  width?: number;
  height?: number;
  uploadedAt: string;
}

interface PresignedUrlResponse {
  uploadUrl: string;
  key: string;
  expiresIn: number;
  maxSize: number;
}

interface SaveImageRequest {
  key: string;
  activityId: string;
  size: number;
  mimeType: string;
  width?: number;
  height?: number;
}

interface SaveImageResponse {
  message: string;
  image: ImageData;
}

/**
 * Get presigned URL for uploading image to R2 (process context)
 */
export async function getPresignedUploadUrl(
  processId: string,
  activityId: string,
  mimeType?: string,
  retryFetch?: (url: string, options?: RequestInit) => Promise<Response>
): Promise<PresignedUrlResponse> {
  const url = `${API_BASE_URL}/api/images/presigned-url`;
  const options: RequestInit = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify({ processId, activityId, mimeType }),
  };

  const response = retryFetch
    ? await retryFetch(url, options)
    : await fetch(url, options);

  if (!response.ok) {
    if (response.status === 429) {
      const error = await response.json();
      throw new Error(error.message || 'Rate limit exceeded. Please try again later.');
    }
    const error = await response.json().catch(() => ({ error: 'Failed to get presigned URL' }));
    throw new Error(error.error || 'Failed to get presigned URL');
  }

  return response.json();
}

/**
 * Upload image file directly to R2 using presigned URL
 */
export async function uploadImageToR2(
  uploadUrl: string,
  file: File,
  mimeType: string
): Promise<void> {
  const response = await fetch(uploadUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': mimeType,
    },
    body: file,
  });

  if (!response.ok) {
    throw new Error(`Failed to upload to R2: ${response.statusText}`);
  }
}

/**
 * Save image metadata to backend after successful upload (process context)
 */
export async function saveImageMetadata(
  processId: string,
  data: SaveImageRequest,
  retryFetch?: (url: string, options?: RequestInit) => Promise<Response>
): Promise<SaveImageResponse> {
  const url = `${API_BASE_URL}/api/images/processes/${processId}/images`;
  const options: RequestInit = {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify(data),
  };

  const response = retryFetch
    ? await retryFetch(url, options)
    : await fetch(url, options);

  if (!response.ok) {
    if (response.status === 413) {
      const error = await response.json();
      throw new Error(error.error || 'File size exceeds limit');
    }
    const error = await response.json().catch(() => ({ error: 'Failed to save image metadata' }));
    throw new Error(error.error || 'Failed to save image metadata');
  }

  return response.json();
}

/**
 * Delete image from R2 and remove metadata (process context)
 */
export async function deleteImage(
  processId: string,
  activityId: string,
  retryFetch?: (url: string, options?: RequestInit) => Promise<Response>
): Promise<void> {
  const url = `${API_BASE_URL}/api/images/processes/${processId}/images`;
  const options: RequestInit = {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify({ activityId }),
  };

  const response = retryFetch
    ? await retryFetch(url, options)
    : await fetch(url, options);

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to delete image' }));
    throw new Error(error.error || 'Failed to delete image');
  }
}
